// Calculadoras pediátricas automáticas
export function bmi(weightKg, heightCm) {
  if (!weightKg || !heightCm) return null;
  return +(weightKg / Math.pow(heightCm / 100, 2)).toFixed(1);
}

export function bsaMosteller(weightKg, heightCm) {
  if (!weightKg || !heightCm) return null;
  return +Math.sqrt((heightCm * weightKg) / 3600).toFixed(2);
}

// Mantenimiento de líquidos Holliday-Segar (ml/día y ml/h)
export function maintenanceFluids(weightKg) {
  if (!weightKg) return null;
  let mlDay = 0;
  if (weightKg <= 10) mlDay = weightKg * 100;
  else if (weightKg <= 20) mlDay = 1000 + (weightKg - 10) * 50;
  else mlDay = 1500 + (weightKg - 20) * 20;
  return { mlDay: Math.round(mlDay), mlHour: Math.round(mlDay / 24) };
}

// Dosis por peso: mg/kg/día dividido en tomas
export function doseByWeight(weightKg, mgPerKgDay, doses) {
  if (!weightKg || !mgPerKgDay) return null;
  const total = weightKg * mgPerKgDay;
  const perDose = doses ? total / doses : total;
  return { totalDay: +total.toFixed(1), perDose: +perDose.toFixed(1) };
}

// Extrae la concentración (mg por mL) de una presentación en texto.
// Ej: "solución gotas 100mg/ml" -> 100 ; "suspensión 250mg/5ml" -> 50
export function concentrationMgPerMl(presentation) {
  if (!presentation) return null;
  const s = String(presentation).toLowerCase().replace(/\s+/g, "");
  const m = s.match(/(\d+(?:\.\d+)?)mg\/(\d+(?:\.\d+)?)?ml/);
  if (m) {
    const mg = parseFloat(m[1]);
    const ml = m[2] ? parseFloat(m[2]) : 1;
    if (mg && ml) return mg / ml;
  }
  return null;
}

// Calcula la dosis concreta por toma a partir de una regla mg/kg (ej "10-15 mg/kg/dosis"),
// el peso del paciente y la presentación. Devuelve { mgText, mlText, text } o null.
export function doseFromMgKgRule(mgKgStr, weightKg, presentation) {
  const w = parseFloat(weightKg);
  if (!mgKgStr || !w) return null;
  const nums = (String(mgKgStr).match(/\d+(?:\.\d+)?/g) || []).map(Number);
  if (!nums.length) return null;
  const low = nums[0];
  const high = nums.length > 1 ? nums[1] : nums[0];
  const mgLow = +(low * w).toFixed(1);
  const mgHigh = +(high * w).toFixed(1);
  const mgText = mgLow === mgHigh ? `${mgLow} mg` : `${mgLow}–${mgHigh} mg`;
  const conc = concentrationMgPerMl(presentation);
  let mlText = null;
  if (conc) {
    const mlLow = +(mgLow / conc).toFixed(1);
    const mlHigh = +(mgHigh / conc).toFixed(1);
    mlText = mlLow === mlHigh ? `${mlLow} mL` : `${mlLow}–${mlHigh} mL`;
  }
  return { mgText, mlText, text: mlText ? `${mlText} (${mgText})` : mgText };
}


// Fármacos pediátricos comunes (mg/kg/día)
export const COMMON_DRUGS = [
  { name: "Paracetamol", mgPerKgDose: 15, doses: 4, byDose: true, note: "10-15 mg/kg/dosis c/6h" },
  { name: "Ibuprofeno", mgPerKgDose: 10, doses: 3, byDose: true, note: "5-10 mg/kg/dosis c/8h" },
  { name: "Amoxicilina", mgPerKgDay: 50, doses: 3, note: "40-50 mg/kg/día c/8h" },
  { name: "Amoxi/Clav", mgPerKgDay: 45, doses: 2, note: "45 mg/kg/día c/12h" },
  { name: "Azitromicina", mgPerKgDay: 10, doses: 1, note: "10 mg/kg/día x3-5d" },
  { name: "Cefalexina", mgPerKgDay: 50, doses: 4, note: "25-50 mg/kg/día c/6h" },
];

export function ageFromBirth(birthIso) {
  const bd = new Date(birthIso);
  const now = new Date();
  const days = Math.floor((now - bd) / 86400000);
  const months = Math.floor(days / 30.4375);
  const years = Math.floor(months / 12);
  const rem = months % 12;
  let text;
  if (years >= 1) {
    const yLbl = years === 1 ? "1 año" : `${years} años`;
    text = rem > 0 ? `${yLbl} ${rem} ${rem === 1 ? "mes" : "meses"}` : yLbl;
  } else {
    text = `${months} ${months === 1 ? "mes" : "meses"}`;
  }
  return { days, months, years, text };
}

export function isAdult(birthIso) {
  return ageFromBirth(birthIso).years >= 18;
}

export function correctedAgeMonths(birthIso, gestationalWeeks) {
  const m = ageFromBirth(birthIso).months;
  if (gestationalWeeks && gestationalWeeks < 37 && m < 24) {
    return Math.max(0, m - Math.round((40 - gestationalWeeks) / 4.345));
  }
  return m;
}

function vitalRanges(m) {
  if (m < 3) return { hr: [100, 205], rr: [30, 60] };
  if (m < 12) return { hr: [90, 190], rr: [24, 40] };
  if (m < 24) return { hr: [90, 150], rr: [20, 37] };
  if (m < 60) return { hr: [70, 130], rr: [18, 30] };
  if (m < 144) return { hr: [60, 120], rr: [16, 24] };
  return { hr: [55, 100], rr: [12, 20] };
}

export function evalVitals(ageMonths, { hr, rr, temp, spo2 }) {
  const r = vitalRanges(ageMonths);
  const out = [];
  const push = (key, label, value, unit, range, ok) => {
    if (value === "" || value == null || isNaN(value)) return;
    out.push({ key, label, value, unit, range, status: ok ? "normal" : "alarma" });
  };
  const has = (v) => v !== undefined && v !== null && String(v).trim() !== "";
  if (has(hr)) push("hr", "FC", +hr, "lpm", `${r.hr[0]}-${r.hr[1]}`, +hr >= r.hr[0] && +hr <= r.hr[1]);
  if (has(rr)) push("rr", "FR", +rr, "rpm", `${r.rr[0]}-${r.rr[1]}`, +rr >= r.rr[0] && +rr <= r.rr[1]);
  if (has(temp)) push("temp", "Temp", +temp, "°C", "36-38", +temp >= 36 && +temp < 38);
  if (has(spo2)) push("spo2", "SatO₂", +spo2, "%", "≥94", +spo2 >= 94);
  return out;
}

export const METRICS = [
  { key: "peso", label: "Peso", unit: "kg" },
  { key: "talla", label: "Talla", unit: "cm" },
  { key: "perimetro_cefalico", label: "P. Cefálico", unit: "cm" },
  { key: "imc", label: "IMC", unit: "kg/m²" },
];

// NOM-004: evaluación local (sin IA) del cumplimiento de la nota de evolución.
const CIE10_LOCAL = /\b[A-TV-Z]\d{2}(?:\.[0-9A-Z]{1,4})?\b/;
export function nom004Local(c = {}) {
  const s = (v) => (v == null ? "" : String(v)).trim();
  const reason = s(c.reason) || s(c.current_illness);
  const dx = s(c.diagnosis);
  const hasSoma = !!(c.weight || c.height || c.head_circ);
  const hasVitals = !!(c.temperature || c.heart_rate || c.resp_rate || c.spo2 || c.blood_pressure);
  const items = [
    { key: "motivo", label: "Motivo de consulta", ok: !!reason },
    { key: "padecimiento", label: "Padecimiento actual", ok: !!s(c.current_illness) },
    { key: "exploracion", label: "Exploración física", ok: !!s(c.physical_exam) },
    { key: "diagnostico", label: "Diagnóstico", ok: !!dx },
    { key: "cie10", label: "Diagnóstico codificado (CIE-10)", ok: !!(dx && CIE10_LOCAL.test(dx)) },
    { key: "plan", label: "Plan de tratamiento", ok: !!s(c.treatment) },
    { key: "somatometria", label: "Somatometría o signos vitales", ok: hasSoma || hasVitals },
  ];
  const passed = items.filter((i) => i.ok).length;
  const score = Math.round((passed / items.length) * 100);
  const level = score >= 85 ? "verde" : score >= 60 ? "amarillo" : "rojo";
  return { items, score, level, passed, total: items.length };
}
